import type { MeResponse, ProductsResponse } from '@stalvi/contracts';
import { isEan13 } from '@stalvi/contracts';
import { useQuery } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FiltersSheet } from '../components/FiltersSheet';
import { Header } from '../components/Header';
import { ProductThumb } from '../components/ProductThumb';
import { api } from '../lib/api';
import { formatCLP } from '../lib/format';
import { useSearchBackend } from '../lib/searchBackend';

const PAGE_SIZE = 25;

function PageButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className="btn btn-secondary btn-sm" disabled={disabled} onClick={onClick}>
      {label}
    </button>
  );
}

export function List({ me, onSignOut }: { me: MeResponse; onSignOut: () => void }) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const brands = useMemo(() => (params.get('brands') ?? '').split(',').filter(Boolean), [params]);
  // Guard a non-numeric ?page (bookmarked/hand-edited URL): Number('abc') is NaN
  // and Math.max(1, NaN) is NaN, which breaks every pagination guard below.
  const pageParam = Number(params.get('page'));
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? Math.floor(pageParam) : 1;
  const nav = useNavigate();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const isEanQuery = isEan13(q);

  // `backend` is part of the key so flipping the bake-off toggle (#199) refetches
  // the same query through the other backend.
  const backend = useSearchBackend();
  const queryKey = ['products', { q, brands, page, backend }];
  const { data, isLoading, error } = useQuery<ProductsResponse>({
    queryKey,
    queryFn: () =>
      api.products({
        q: q || undefined,
        brands,
        page,
      }),
  });

  // Clones the current URL params, lets the caller mutate the copy, and
  // pushes the result through `setParams`. Keeps the toggle/page handlers
  // focused on the mutation itself instead of the clone+set boilerplate.
  function updateParams(mutator: (p: URLSearchParams) => void) {
    const p = new URLSearchParams(params);
    mutator(p);
    setParams(p);
  }

  function toggleBrand(id: string) {
    const next = new Set(brands);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    updateParams((p) => {
      if (next.size) p.set('brands', Array.from(next).join(','));
      else p.delete('brands');
      p.delete('page');
    });
  }

  function goPage(n: number) {
    updateParams((p) => p.set('page', String(n)));
  }

  function clearFilters() {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    setParams(p);
  }

  const cleanupDoneRef = useRef<ProductsResponse | null>(null);
  useEffect(() => {
    if (!data) return;
    if (cleanupDoneRef.current === data) return;
    cleanupDoneRef.current = data;
    const knownBrandIds = new Set(data.facets.brands.map((b) => b.id));
    const rawBrands = (params.get('brands') ?? '').split(',').filter(Boolean);
    const cleanedBrands = rawBrands.filter((id) => knownBrandIds.has(id));
    const brandsChanged = cleanedBrands.length !== rawBrands.length;
    if (!brandsChanged) return;
    const cleaned = new URLSearchParams(params);
    if (cleanedBrands.length) cleaned.set('brands', cleanedBrands.join(','));
    else cleaned.delete('brands');
    setParams(cleaned, { replace: true });
  }, [data, params, setParams]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasActiveFilters = brands.length > 0;
  const showEanEmpty = isEanQuery && total === 0 && !isLoading;

  return (
    <div className="stalvi-app flex flex-col">
      <Header me={me} onSignOut={onSignOut} />
      <div className="app-header" style={{ height: 56, borderTop: '1px solid var(--border-subtle)' }}>
        <input
          className="input"
          style={{ maxWidth: 420 }}
          placeholder="Busca por EAN13, SKU o descripción"
          defaultValue={q}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const v = (e.target as HTMLInputElement).value.trim();
              const p = new URLSearchParams();
              if (v) p.set('q', v);
              setParams(p);
            }
          }}
          aria-label="Refinar búsqueda"
        />
        <span className="text-sm text-text-secondary">
          {isLoading ? 'Cargando…' : `${total.toLocaleString('es-CL')} resultados`}
        </span>
      </div>
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 px-6 py-6 overflow-auto">
        {showEanEmpty ? (
          <div className="lg:col-span-2 text-center py-12">
            <p className="text-text-primary mb-4">Sin resultados para EAN {q}</p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                const p = new URLSearchParams();
                setParams(p);
                nav('/');
              }}
            >
              Buscar por descripción
            </button>
          </div>
        ) : (
          <>
            <FiltersSheet
              brands={data?.facets.brands ?? []}
              selectedBrands={brands}
              onToggleBrand={toggleBrand}
              onClearFilters={clearFilters}
              hasActiveFilters={hasActiveFilters}
              mobileOpen={mobileFiltersOpen}
              onMobileClose={() => setMobileFiltersOpen(false)}
            />
            <section>
              <button
                type="button"
                className="btn btn-secondary btn-sm mb-4 lg:hidden"
                onClick={() => setMobileFiltersOpen(true)}
                aria-label="Abrir filtros"
              >
                <SlidersHorizontal size={16} />
                Filtros
                {hasActiveFilters && <span className="pill ml-1">{brands.length}</span>}
              </button>
              {error && (
                <p role="alert" className="text-danger mb-4">
                  Hubo un problema, intenta de nuevo.
                </p>
              )}
              {total === 0 && !isLoading ? (
                <div className="card p-8 text-center">
                  <p className="text-text-primary">
                    {q
                      ? `No encontramos coincidencias para «${q}». Revisa la ortografía o intenta con otra palabra.`
                      : 'No hay productos con estos filtros'}
                  </p>
                  {hasActiveFilters && (
                    <button type="button" className="btn btn-secondary mt-4" onClick={clearFilters}>
                      Limpiar filtros
                    </button>
                  )}
                </div>
              ) : (
                <ul className="card p-0 divide-y divide-border-subtle">
                  {(data?.results ?? []).map((r) => {
                    // prices[] arrives cheapest-first, but a retailer with a null
                    // best_price can sort to the front (BigQuery orders NULLs first),
                    // so take the first entry that actually carries a price.
                    const lowest = r.prices.find((p) => p.bestPrice != null)?.bestPrice ?? null;
                    return (
                      <li key={r.productId}>
                        <Link
                          to={`/product/${r.productId}`}
                          className="grid grid-cols-[56px_1fr] sm:grid-cols-[64px_1fr_auto] items-start gap-3 sm:gap-4 px-5 py-4 hover:bg-bg-subtle transition-colors"
                        >
                          <ProductThumb src={r.imageUrl ?? null} alt={r.name} />
                          <div className="min-w-0">
                            <p className="text-[15px] font-medium leading-snug text-text-primary line-clamp-2">
                              {r.name}
                            </p>
                            <p className="text-[13px] text-text-secondary truncate mt-0.5">
                              {r.brand ?? '—'}
                            </p>
                            {r.packaging && (
                              <span className="inline-block text-[11px] px-1.5 py-0.5 rounded bg-bg-subtle text-text-tertiary mt-1.5">
                                {r.packaging}
                              </span>
                            )}
                            {r.ean13 && (
                              <span className="mono tabular-nums text-[11px] text-text-tertiary mt-1.5 block sm:hidden">
                                {r.ean13}
                              </span>
                            )}
                          </div>
                          <div className="self-start text-right hidden sm:block">
                            {lowest != null && (
                              <span className="mono tabular-nums text-[15px] font-semibold text-text-primary whitespace-nowrap">
                                {formatCLP(lowest)}
                              </span>
                            )}
                            {r.ean13 && (
                              <span className="mono tabular-nums text-[11px] text-text-tertiary tracking-tight whitespace-nowrap block mt-0.5">
                                {r.ean13}
                              </span>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}

              {totalPages > 1 && (
                <nav className="flex items-center gap-2 mt-4 flex-wrap" aria-label="Paginación">
                  <PageButton label="Primera" disabled={page <= 1} onClick={() => goPage(1)} />
                  <PageButton label="Anterior" disabled={page <= 1} onClick={() => goPage(page - 1)} />
                  <span className="text-sm text-text-secondary px-2">
                    Página {page} de {totalPages}
                  </span>
                  <PageButton
                    label="Siguiente"
                    disabled={page >= totalPages}
                    onClick={() => goPage(page + 1)}
                  />
                  <PageButton
                    label="Última"
                    disabled={page >= totalPages}
                    onClick={() => goPage(totalPages)}
                  />
                </nav>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
